import React, { useEffect, useRef, useState } from "react";
import { apiUrls, getApiUrl } from "../../config";
import axios from "axios";
import { env, useGlobalState } from "../../helpers/GlobalStateProvider";
import { useModalState } from "../../helpers/ModalProvider";
import { DialogCloseTrigger, DialogContent, DialogHeader, DialogRoot } from "../../components/ui/dialog";
import { CardBody, CardHeader, CardRoot, DialogBody, DialogFooter, DialogTitle, Flex, Heading, Stack, Text } from "@chakra-ui/react";
import { Button } from "../../components/ui/button";
import { getToken } from "../../helpers/tokenManager";

const DeleteApiKeyModal: React.FC = () => {
  const {
    isDeleteModalVisible,
    setDeleteModalVisible,
    modalContent,
    showErrorMessage,
  } = useModalState();

  const [isLoading, setIsLoading] = useState(false);

  const label = modalContent.label;
  const apiKey = modalContent.apiKey;

  const { refreshAccountInfo } = useGlobalState();

  // Ref to the delete button so we can focus it when the modal opens. This
  // gives a visible focus indicator AND makes the scoped Enter-to-confirm
  // behaviour feel natural: Enter activates the focused button rather than
  // firing from anywhere on the page.
  const deleteButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isDeleteModalVisible) return;
    // Defer focus until after the dialog mounts.
    const id = window.setTimeout(() => {
      deleteButtonRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(id);
  }, [isDeleteModalVisible]);


  const handleDeleteApiKey = async () => {
    try {
      setIsLoading(true);
      const bearerToken = getToken();
      if (!bearerToken) return;

      const requestBody = {
        apiKey,
        action: "disable",
        label,
      };

      await axios.post(`${getApiUrl(env)}/v3/apiKey/toggleApiKey`, requestBody, {
        headers: { Authorization: `Bearer ${bearerToken}` },
      });

      await refreshAccountInfo();
    } catch (error: any) {
      showErrorMessage(
        `An unexpected error occurred. ${error?.response?.data?.message}`,
      );
    } finally {
      setIsLoading(false);
      setDeleteModalVisible(false);
    }
  };

  return (
    <DialogRoot lazyMount open={isDeleteModalVisible} onOpenChange={e => setDeleteModalVisible(e.open)}>
      <DialogContent
        onKeyDown={(e) => {
          // Scope Enter-to-delete to the modal content only (previously this was
          // a global document listener that fired on any Enter press anywhere on
          // the page while the modal was open).
          if (e.key === "Enter" && !isLoading) {
            e.preventDefault();
            handleDeleteApiKey();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Delete API Key</DialogTitle>
          <DialogCloseTrigger />
        </DialogHeader>
        <DialogBody>
          <Stack>
            <CardRoot variant='subtle'>
              <CardHeader pb={6} borderBottomWidth={1} borderColor='border'>
                <Heading size='md'>Are you sure you want to delete this API key?</Heading>
                <Text color='fg.muted'>This action cannot be undone.</Text>
              </CardHeader>
              <CardBody gap={4}>
                <Flex justifyContent="space-between">
                  <Text>Key Label</Text>
                  <Text fontWeight='bold'>{label}</Text>
                </Flex>
                <Flex gap={4} justifyContent="space-between">
                  <Text>API Key</Text>
                  <Text flex={1} overflow='hidden' textOverflow='ellipsis' whiteSpace='nowrap'>{apiKey}</Text>
                </Flex>
              </CardBody>
            </CardRoot>
          </Stack>
        </DialogBody>
        <DialogFooter>
          <Button ref={deleteButtonRef} colorPalette='red' onClick={handleDeleteApiKey} loading={isLoading}>Delete Key</Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};

export default DeleteApiKeyModal;
